import { NavObject, UpdateNavObjAndGetNextScreenParams, UpdateNavObjAndGetNextScreenResponse } from '@usacsmideo/types';
import { enableScreen } from './enableScreen';
import { okToEnableScreen } from './checkIfOkToEnable';
import { getValidScreenArray } from './getValidScreenArray';
import * as Sentry from '@sentry/browser';

export const updateNavObjAndGetNextScreen = async (
  payload: UpdateNavObjAndGetNextScreenParams,
): Promise<UpdateNavObjAndGetNextScreenResponse> => {
  const { patientId, currentPage, patientInfo, chart } = payload;

  // Defense in depth (CU-86ahkkbmb W1). Layout recovery should prevent this,
  // but guarding before getValidScreenArray avoids the Object.keys(undefined)
  // crash on Next-button clicks during the rare race window where a deep route
  // mounts before navObj resolves.
  if (!patientInfo.navObj) {
    const error = new Error(
      `updateNavObjAndGetNextScreen: patientInfo.navObj missing for patient ${patientId}`,
    );
    Sentry.captureException(error, { extra: { patientId, currentPage } });
    throw error;
  }

  // CONVERT THE NAVIGATION OBJECT TO A VALIDATED ARRAY OF SCREEN OBJECTS
  const screenArr = getValidScreenArray(patientInfo.navObj, 'updateNavObjAndGetNextScreen');

  if (screenArr.length === 0) {
    Sentry.captureException(
      new Error(`All navObj entries are malformed for patient ${patientId}`),
    );
    return {
      errorMessage:
        'There was an error loading your navigation data. Please try again later or contact support if this issue persists.',
    };
  }

  // FILTER OUT DISABLED (NON-INDEX) SCREENS
  const enablableScreens = screenArr.filter((screen) => {
    return (
      screen.id.toLowerCase().includes('index') ||
      screen.id.includes('thanks') ||
      // FORCE INCLUSION OF 'DISABLED' RECORDING AND SIGNATURES SCREENS

      // THEY ARE DISABLED BECAUSE WE ALWAYS WANT TO SHOW THEM ON THE INDEX CARD EVEN IF THEY ARE UNAVAILABLE
      screen.id.includes('recording') ||
      screen.id.includes('finalSignatures') ||
      (!screen.id.toLowerCase().includes('index') && !screen.disabled)
    );
  });

  // screenArr is already sorted by baseOrder from getValidScreenArray
  const sorted = enablableScreens;
  // FIND THE CURRENT SCREEN AND PAGE OBJECTS
  const currentScreenIndex = sorted.findIndex(
    (page) => page.path.split('/').slice(1).join('/') === currentPage.split('/').slice(2).join('/'),
  );
  const currentScreenObject = sorted[currentScreenIndex];
  const nextScreenObject = sorted[currentScreenIndex + 1];

  // CURRENT SCREEN NOT FOUND — GENUINE ERROR (BAD ROUTING, STALE URL)
  if (!currentScreenObject) {
    Sentry.captureException(
      new Error(
        `Current screen not found in nav array for patient ${patientId} from page: ${currentPage}`,
      ),
    );
    return {
      errorMessage:
        "We couldn't determine your current position in the navigation. Please try again or contact support if this issue persists.",
    };
  }

  // NO NEXT SCREEN — TERMINAL STATE (PATIENT HAS COMPLETED THE FLOW)
  if (!nextScreenObject) {
    const terminalNavObj: NavObject = {
      ...patientInfo.navObj,
      [currentScreenObject.id]: {
        ...currentScreenObject,
        completed: true,
      },
    };
    return { complete: true, updatedNavObj: terminalNavObj };
  }

  // PREPARE UPDATED NAV OBJECT WITH CURRENT SCREEN MARKED AS COMPLETED
  const updatedNavObj: NavObject = {
    ...patientInfo.navObj,
    [currentScreenObject.id]: {
      ...currentScreenObject,
      completed: true,
    },
  };

  if (patientInfo.navType?.base === 'concierge' && nextScreenObject.displayName.includes('Care')) {
    // MANUALLY SKIP CARE SCREENS FOR CONCIERGE PATIENTS
    return {
      updatedNavObj: updatedNavObj,
      nextScreen: patientInfo.navObj['coverageIndex'],
    };
  } else {
    const checkPayload = { nextScreenObject, updatedNavObj, chart };
    let canEnableScreen = 'no';
    let enabledNavObj = { ...patientInfo.navObj };
    let enableScreenPayload = {
      patientId: patientId,
      newNavObj: { ...patientInfo.navObj },
    };
    let screenIsEnabled = 'no';

    switch (nextScreenObject.disabled) {
      case false:
        // IF THE NEXT SCREEN IS NOT DISABLED, JUST RETURN THE UPDATED NAV OBJECT AND NEXT SCREEN
        return {
          updatedNavObj: updatedNavObj,
          nextScreen: nextScreenObject,
        };
        break;
      case true:
        // USE THE IN MEMORY NAVIGATION OBJECT AND THE CHART PASSED INTO THE FUNCTION TO
        // CHECK FIRST IF SCREEN CAN BE ENABLED ASSUMING THE CURRENT SCREEN IS MARKED AS COMPLETED
        canEnableScreen = okToEnableScreen(checkPayload);
        // CONDITIONS MET. ENABLE THE SCREEN
        switch (canEnableScreen) {
          case 'ok':
            enabledNavObj = {
              ...updatedNavObj,
              [nextScreenObject.id]: {
                ...nextScreenObject,
                disabled: false,
              },
            };
            // ENABLE THE SCREEN IN FIRESTORE
            enableScreenPayload = {
              patientId: patientId,
              newNavObj: enabledNavObj,
            };
            screenIsEnabled = await enableScreen(enableScreenPayload);

            // HANDLE THE RESPONSE FROM ENABLE SCREEN
            switch (screenIsEnabled) {
              case 'enabled':
                return {
                  updatedNavObj: enabledNavObj,
                  nextScreen: nextScreenObject,
                };
              case 'error':
                Sentry.captureException(
                  new Error(`Firestore Error enabling screen for patient ${patientId}`),
                );
                console.error('FirestoreError enabling screen');
                return {
                  errorMessage: `There was an error communicating with the database. Please try again later or contact support if this issue persists.`,
                };
            }
            break;
          default:
            console.error('Incomplete Info:', canEnableScreen);
            return { incompleteMessage: canEnableScreen };
        }
    }
  }
  Sentry.captureException(
    new Error(`An unknown error occurred in updateNavObjAndGetNextScreen for patient ${patientId}`),
  );
  return {
    errorMessage: `An unknown error occurred while retrieving the next screen information.`,
  };
};
